import React, { useEffect, useState } from "react";
import { useApi } from "../hooks/useApi";
import { formatUtcDateTime } from "../utils/dateTime";

/**
 * Step 7: the Exposure Management weaknesses the graph attaches to one asset (identity or device).
 *
 * An empty list has three different meanings and the section always says which one it is: the
 * graph was read and this asset is named by nothing (clean), the tenant has no exposure graph to
 * read (unknown — a licence question), or no sync has read it yet. "We could not look" must never
 * be shown the same way as "nothing found".
 */
// `hideHeading`: the caller already shows "Posture weaknesses" (a click-to-open section in SOC).
export default function PostureWeaknesses({ entity, id, label = "asset", hideHeading = false }) {
  const apiCall = useApi();
  const [data, setData] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    apiCall("GET", `/defender-identity/${entity}/${id}/posture`)
      .then(r => { if (!cancelled) setData(r && Array.isArray(r.weaknesses) ? r : { weaknesses: [], state: "notRead" }); })
      .catch(() => { if (!cancelled) setData({ weaknesses: [], state: "notRead" }); });
    return () => { cancelled = true; };
  }, [apiCall, entity, id]);

  const weaknesses = data?.weaknesses ?? [];
  return (
    <>
      {!hideHeading && <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-4 mb-1">Posture weaknesses</div>}
      {data === null && <div className="text-xs text-gray-400 py-1.5">Loading…</div>}

      {data && data.state === "unavailable" && (
        <div className="text-xs text-amber-700 py-1.5" data-testid="posture-unavailable">
          Not known: {data.message || "the Exposure Management graph could not be read."}
        </div>
      )}
      {data && data.state === "notRead" && (
        <div className="text-xs text-gray-500 py-1.5" data-testid="posture-not-read">
          Not known: no sync has read the Exposure Management graph for this organization yet.
        </div>
      )}
      {data && data.state === "available" && data.message && (
        <div className="text-[11px] text-amber-700 py-1" data-testid="posture-partial">
          Partly observed: {data.message}
        </div>
      )}
      {data && data.state === "available" && !data.message && weaknesses.length === 0 && (
        <div className="text-xs text-gray-400 py-1.5" data-testid="posture-clean">
          The Exposure Management graph names no weakness on this {label}.
        </div>
      )}

      {weaknesses.length > 0 && (
        <ul className="space-y-1 py-1" data-testid="posture-weaknesses">
          {weaknesses.map(w => (
            <li key={w.nodeId} className="text-xs py-1 border-b border-gray-100">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-gray-900 truncate" title={w.name}>{w.name}</span>
                {w.label && <span className="px-1 rounded bg-gray-100 text-gray-600 border border-gray-200 flex-shrink-0">{w.label}</span>}
              </div>
              {/* The graph's own words for the relationship and the weakness category — not ours. */}
              <div className="text-gray-500 mt-0.5 break-all">
                {w.edgeLabel}{w.category ? ` · ${w.category}` : ""}
              </div>
            </li>
          ))}
        </ul>
      )}

      {data && data.state === "available" && weaknesses.length > 0 && !data.inLastSync && (
        <div className="text-[11px] text-amber-700">
          The latest graph read no longer names this {label}
          {data.lastSeenAt ? ` (last named ${formatUtcDateTime(data.lastSeenAt)})` : ""}.
        </div>
      )}
    </>
  );
}
